import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { professionsApi } from '../services/smartBillApi';
import {
  DEFAULT_PROFESSIONS,
  mergeProfessionLists,
  normalizeProfessionName,
  canonicalizeProfessionName,
  professionKey,
  isSpecialProfession,
} from '../utils/professions';

const isBuiltIn = (name) =>
  DEFAULT_PROFESSIONS.some((p) => professionKey(p) === professionKey(name));

/**
 * Shared profession options for Coupons + Smart Bill.
 * Dedupes space-variants (JOB SEEKER === JOBSEEKER).
 * Delete is for cleaning DB rows / custom entries (aalogin admin UI).
 */
export default function useProfessionOptions() {
  const [options, setOptions] = useState(DEFAULT_PROFESSIONS);
  /** All API rows: [{ id, name }] */
  const [apiRows, setApiRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await professionsApi.getActive();
      const rows = (response?.professions || [])
        .filter((p) => p && p.name)
        .map((p) => ({
          id: p.id,
          name: normalizeProfessionName(p.name),
        }));
      setApiRows(rows);
      setOptions(mergeProfessionLists(rows.map((r) => r.name)));
    } catch (err) {
      console.warn('Profession list load failed, using defaults:', err);
      setOptions(DEFAULT_PROFESSIONS);
      setApiRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addProfession = useCallback(
    async (rawName) => {
      const name = canonicalizeProfessionName(rawName);
      if (!name) {
        toast.error('Enter a profession name');
        return null;
      }
      if (isSpecialProfession(name)) {
        toast.error('Reserved name — choose another profession');
        return null;
      }
      const key = professionKey(name);
      const existing = options.find((p) => professionKey(p) === key);
      if (existing) {
        toast.info(`${existing} is already in the list`);
        return existing;
      }

      setAdding(true);
      try {
        const res = await professionsApi.create({ name, is_active: true });
        const createdName = canonicalizeProfessionName(
          res?.profession?.name || name,
        );
        await refresh();
        toast.success(`Added profession: ${createdName}`);
        return createdName;
      } catch (err) {
        const msg = err?.message || '';
        if (/already exists|duplicate/i.test(msg)) {
          await refresh();
          toast.info(`${name} is already available`);
          return name;
        }
        console.warn('Profession create API failed, using local list:', err);
        setOptions((prev) => mergeProfessionLists([...prev, name]));
        toast.success(`Added profession locally: ${name}`);
        return name;
      } finally {
        setAdding(false);
      }
    },
    [options, refresh],
  );

  const getDeletableRows = useCallback(
    (rawName) => {
      const key = professionKey(rawName);
      const matches = apiRows.filter((r) => professionKey(r.name) === key);
      if (!isBuiltIn(rawName)) {
        return matches;
      }
      // Built-in: only delete non-canonical spellings (e.g. "JOB SEEKER")
      const canonical = canonicalizeProfessionName(rawName);
      return matches.filter(
        (r) => normalizeProfessionName(r.name) !== canonical,
      );
    },
    [apiRows],
  );

  const canDeleteProfession = useCallback(
    (rawName) => {
      const key = professionKey(rawName);
      if (!key) return false;
      const deletable = getDeletableRows(rawName);
      if (deletable.length > 0) return true;
      // Custom local-only entry (not built-in, not in API)
      if (!isBuiltIn(rawName) && !apiRows.some((r) => professionKey(r.name) === key)) {
        return true;
      }
      return false;
    },
    [apiRows, getDeletableRows],
  );

  const deleteProfession = useCallback(
    async (rawName, { hardDelete = true } = {}) => {
      const name = normalizeProfessionName(rawName);
      if (!name) return false;

      const key = professionKey(name);
      const targets = getDeletableRows(name);

      if (targets.length === 0) {
        if (isBuiltIn(name)) {
          toast.info(
            `${canonicalizeProfessionName(name)} is built-in. Only duplicate spellings can be deleted.`,
          );
          return false;
        }
        // Local-only custom
        setOptions((prev) => prev.filter((p) => professionKey(p) !== key));
        toast.success(`Removed ${name} from list`);
        return true;
      }

      setDeleting(true);
      try {
        for (const row of targets) {
          await professionsApi.delete(row.id, hardDelete);
        }
        await refresh();
        toast.success(
          `Deleted: ${targets.map((r) => r.name).join(', ')}`,
        );
        return true;
      } catch (err) {
        console.error('Profession delete failed:', err);
        toast.error(err?.message || 'Failed to delete profession');
        return false;
      } finally {
        setDeleting(false);
      }
    },
    [getDeletableRows, refresh],
  );

  return {
    options,
    loading,
    adding,
    deleting,
    refresh,
    addProfession,
    deleteProfession,
    canDeleteProfession,
    apiRows,
  };
}
